import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Address, SavedAddress } from "../lib/types";

function newId(): string {
  const c = globalThis.crypto;
  return c && "randomUUID" in c ? c.randomUUID() : "a_" + performance.now().toString(36);
}

/**
 * Customer account. Login is OPTIONAL — guests can always order without it.
 *
 * Demo mode (no backend): a lightweight name + phone "account" persisted in
 * localStorage, so accounts / order history are demonstrable now.
 * Backend go-live: swap `signIn` for Firebase Phone Auth (SMS OTP) and load the
 * profile from `users/{uid}` — the rest of the UI stays the same.
 */
export interface AppUser {
  uid: string;
  name: string;
  phone: string;
  defaultAddress?: Address;
  /** Address book (multiple saved delivery addresses). */
  addresses?: SavedAddress[];
}

interface AuthCtx {
  user: AppUser | null;
  /** Demo sign-in (name + phone). Real OTP replaces this at go-live. */
  signIn: (name: string, phone: string) => void;
  signOut: () => void;
  saveProfile: (patch: Partial<AppUser>) => void;
  /** Add a new address (auto-assigns an id) and return it. */
  addAddress: (addr: Address, label: string) => SavedAddress;
  /** Replace an existing saved address by id. */
  updateAddress: (id: string, patch: Partial<SavedAddress>) => void;
  removeAddress: (id: string) => void;
}

const Ctx = createContext<AuthCtx | null>(null);
const STORAGE_KEY = "kb_user";

function load(): AppUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AppUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(load);

  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  const signIn = useCallback((name: string, phone: string) => {
    const cleanPhone = phone.trim();
    setUser({
      uid: "u_" + cleanPhone.replace(/[^0-9]/g, ""),
      name: name.trim(),
      phone: cleanPhone,
    });
  }, []);

  const signOut = useCallback(() => setUser(null), []);

  const saveProfile = useCallback(
    (patch: Partial<AppUser>) => setUser((u) => (u ? { ...u, ...patch } : u)),
    [],
  );

  const addAddress = useCallback((addr: Address, label: string): SavedAddress => {
    const saved: SavedAddress = { ...addr, id: newId(), label: label.trim() || "Address" };
    setUser((u) => (u ? { ...u, addresses: [...(u.addresses ?? []), saved] } : u));
    return saved;
  }, []);

  const updateAddress = useCallback((id: string, patch: Partial<SavedAddress>) => {
    setUser((u) =>
      u
        ? {
            ...u,
            addresses: (u.addresses ?? []).map((a) =>
              a.id === id ? { ...a, ...patch, id } : a,
            ),
          }
        : u,
    );
  }, []);

  const removeAddress = useCallback((id: string) => {
    setUser((u) =>
      u ? { ...u, addresses: (u.addresses ?? []).filter((a) => a.id !== id) } : u,
    );
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      signIn,
      signOut,
      saveProfile,
      addAddress,
      updateAddress,
      removeAddress,
    }),
    [user, signIn, signOut, saveProfile, addAddress, updateAddress, removeAddress],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
